import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { routes } from '@constants';
import { useScreenSize } from '@hooks';
import { Button, Flex, theme, Typography } from '@styles';
import {
  arrayOf,
  bool,
  func,
  node,
  object,
  oneOfType,
  string,
} from 'prop-types';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';

import { responsive } from './Carousel.layoutProps';
import CarouselButtons from './CarouselButtons';

const CarouselContainer = ({
  containerProps,
  description,
  infinite,
  items,
  openLoginModal,
  renderItem,
  title,
  withButtons,
  withOnboardingWarning,
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { isMobile, isTabletSM } = useScreenSize();
  const carouselRef = useRef(null);

  const { isOnboardingPassed, jwtAuthenticationToken } = useSelector(
    (state) => state.authSlice
  );

  const goToOnboarding = () => {
    if (!jwtAuthenticationToken) {
      openLoginModal(true);
    } else {
      navigate(routes.onboarding);
    }
  };

  return (
    <Flex
      className="carousel-container"
      flexDirection="column"
      flexGrow={0}
      position="relative"
      {...containerProps}
    >
      <Flex flexDirection="column" mb={isMobile ? 0 : 32}>
        {typeof title !== 'string' ? (
          title
        ) : (
          <Typography
            color={theme.colors.white}
            height={isMobile ? 84 : 129}
            variant={isMobile ? 'BN-bold-s80-lh84' : 'BN-bold-s123-lh104'}
            {...containerProps.title}
          >
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            color={theme.colors.white[100]}
            opacity={0.6}
            variant="MS-regular-s16-lh25"
            width={765}
          >
            {description}
          </Typography>
        )}
        {withOnboardingWarning && !isOnboardingPassed && (
          <Flex
            alignItems="center"
            backgroundColor={theme.colors.white[10]}
            flexDirection={isTabletSM ? 'column' : 'row'}
            gap={24}
            justifyContent="space-between"
            my={24}
            p={24}
            width="100%"
          >
            <Typography
              color={theme.colors.white[50]}
              variant="MS-regular-s24-lh37"
              width="100%"
            >
              {i18n.t('SoldierLevelPage.OnboardingWarning.Title')}
            </Typography>
            <Button
              maxWidth={isTabletSM ? '100%' : 200}
              onClick={goToOnboarding}
              py={22}
              variant="primary"
              width="100%"
            >
              <Typography
                color={theme.colors.black[100]}
                variant="MS-semibold-s16-lh25"
              >
                {i18n.t('SoldierLevelPage.OnboardingWarning.Button')}
              </Typography>
            </Button>
          </Flex>
        )}
      </Flex>
      {withButtons && (
        <CarouselButtons
          next={carouselRef?.current?.slickNext}
          previous={carouselRef?.current?.slickPrev}
          {...containerProps.buttons}
        />
      )}
      <Slider
        ref={carouselRef}
        accessibility={false}
        arrows={false}
        centerMode={false}
        className="inner-carousel-container"
        dots={false}
        draggable={false} // desktop
        infinite={infinite}
        responsive={responsive}
        rows={1}
        rtl={false}
        showDots={false}
        slidesToScroll={1}
        slidesToShow={1}
        speed={1000}
        swipe
        swipeToSlide
        variableWidth
      >
        {items?.map(renderItem)}
      </Slider>
    </Flex>
  );
};

CarouselContainer.propTypes = {
  containerProps: object,
  description: string,
  infinite: bool,
  items: arrayOf(object).isRequired,
  openLoginModal: func,
  renderItem: func.isRequired,
  title: oneOfType([string, node]).isRequired,
  withButtons: bool,
  withOnboardingWarning: bool,
};

CarouselContainer.defaultProps = {
  containerProps: {},
  description: null,
  infinite: false,
  openLoginModal: () => {},
  withButtons: true,
  withOnboardingWarning: false,
};

export default CarouselContainer;
